/**
 * TrainingAIAssistant - AI-powered training plan generator
 * For admin use to create personalized training plans
 */
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Sparkles, Dumbbell, Target, Calendar, Clock, User, 
  Send, Loader2, Save, ChevronRight, RefreshCw, Brain,
  Activity, Flame, Heart, Scale, Zap
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GOALS = [
  { id: 'muskelaufbau', label: 'Muskelaufbau', icon: Dumbbell, color: 'text-blue-600', bg: 'bg-blue-100' },
  { id: 'abnehmen', label: 'Abnehmen', icon: Flame, color: 'text-orange-600', bg: 'bg-orange-100' },
  { id: 'ausdauer', label: 'Ausdauer', icon: Heart, color: 'text-red-600', bg: 'bg-red-100' },
  { id: 'kraft', label: 'Kraft', icon: Zap, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  { id: 'flexibilität', label: 'Beweglichkeit', icon: Activity, color: 'text-purple-600', bg: 'bg-purple-100' },
  { id: 'allgemein', label: 'Allgemein', icon: Target, color: 'text-green-600', bg: 'bg-green-100' }
];

const EQUIPMENT = [
  { id: 'gym', label: 'Fitnessstudio', description: 'Alle Geräte verfügbar' },
  { id: 'bodyweight', label: 'Körpergewicht', description: 'Keine Geräte nötig' },
  { id: 'home', label: 'Home-Gym', description: 'Hanteln, Bänder, etc.' },
  { id: 'mixed', label: 'Gemischt', description: 'Körpergewicht + Gewichte' }
];

const EXPERIENCE = [
  { id: 'anfänger', label: 'Anfänger', description: '< 6 Monate Training' },
  { id: 'fortgeschritten', label: 'Fortgeschritten', description: '6 Monate - 2 Jahre' },
  { id: 'profi', label: 'Profi', description: '> 2 Jahre Erfahrung' }
];

const TrainingAIAssistant = ({ token, customers, onPlanCreated }) => {
  const [step, setStep] = useState(1); // 1: Goal, 2: Details, 3: Generate, 4: Review
  const [loading, setLoading] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [aiResponse, setAiResponse] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    goal: '',
    equipment: '',
    days_per_week: 3,
    session_duration: 60,
    experience_level: 'anfänger',
    restrictions: '',
    preferences: '',
    customer_id: ''
  });
  
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  
  // Filtered customers based on search
  const filteredCustomers = customers?.filter(c => {
    if (!customerSearch) return true;
    const searchLower = customerSearch.toLowerCase();
    const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
    const email = (c.email || '').toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  }) || [];
  
  // Chat mode
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sessionId] = useState(() => `training-${Date.now()}`);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const generatePlan = async () => {
    if (!formData.goal || !formData.equipment) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    
    setLoading(true);
    setStep(3);
    
    try {
      const requestPayload = {
        ...formData,
        quick_mode: true
      };

      const response = await axios.post(
        `${API}/training-assistant/generate-plan`,
        requestPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAiResponse(response.data.response);
      setGeneratedPlan(response.data.plan_data);
      setStep(4);

      if (response.data?.plan_quality === 'quick' && response.data?.detail_in_progress) {
        toast.info('Schnellplan erstellt – Detailplan wird im Hintergrund nachgeladen…');

        setTimeout(async () => {
          try {
            const detailResponse = await axios.post(
              `${API}/training-assistant/generate-plan`,
              { ...formData, quick_mode: false },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (detailResponse.data?.plan_quality === 'detailed' && detailResponse.data?.plan_data) {
              setAiResponse(detailResponse.data.response || 'Detailplan bereit');
              setGeneratedPlan(detailResponse.data.plan_data);
              toast.success('Detailplan ist jetzt bereit.');
            }
          } catch (detailError) {
            console.warn('Training detail plan refresh skipped:', detailError);
          }
        }, 3500);
      }
    } catch (error) {
      console.error('Generate plan error:', error);
      toast.error('Fehler bei der Planerstellung');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const savePlan = async () => {
    if (!formData.customer_id) {
      toast.error('Bitte einen Kunden auswählen');
      return;
    }
    
    if (!generatedPlan) {
      toast.error('Kein Plan zum Speichern vorhanden');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(
        `${API}/training-assistant/save-plan?customer_id=${formData.customer_id}`,
        generatedPlan,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Trainingsplan gespeichert und zugewiesen!');
      if (onPlanCreated) onPlanCreated();
      
      // Reset
      setStep(1);
      setFormData({
        goal: '',
        equipment: '',
        days_per_week: 3,
        session_duration: 60,
        experience_level: 'anfänger',
        restrictions: '',
        preferences: '',
        customer_id: ''
      });
      setGeneratedPlan(null);
      setAiResponse('');
    } catch (error) {
      console.error('Save plan error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/training-assistant/chat`,
        {
          message: userMessage,
          session_id: sessionId,
          customer_id: formData.customer_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        plan_data: response.data.plan_data
      }]);
      
      if (response.data.plan_data) {
        setGeneratedPlan(response.data.plan_data);
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Chat-Fehler');
    } finally {
      setLoading(false);
    }
  };

  const resetAssistant = () => {
    setStep(1);
    setFormData({
      goal: '',
      equipment: '',
      days_per_week: 3,
      session_duration: 60,
      experience_level: 'anfänger',
      restrictions: '',
      preferences: '',
      customer_id: ''
    });
    setGeneratedPlan(null);
    setAiResponse('');
    setChatMessages([]);
    setChatMode(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">KI-Trainingsplan Assistent</h2>
            <p className="text-sm text-gray-500">Erstelle personalisierte Trainingspläne mit KI</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setChatMode(!chatMode)}
          >
            {chatMode ? 'Formular-Modus' : 'Chat-Modus'}
          </Button>
          <Button variant="ghost" size="sm" onClick={resetAssistant}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Neu starten
          </Button>
        </div>
      </div>

      {/* Chat Mode */}
      {chatMode ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Chat mit dem KI-Trainer
            </CardTitle>
            <CardDescription>
              Beschreibe, was für einen Trainingsplan du erstellen möchtest
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Customer Selection with Search */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <Label className="mb-2 block">Kunde auswählen</Label>
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    placeholder="Kunde suchen..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full"
                  />
                  <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
                {formData.customer_id && (
                  <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                    <User className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-900">
                      {customers?.find(c => c.id === formData.customer_id)?.first_name}{' '}
                      {customers?.find(c => c.id === formData.customer_id)?.last_name}
                    </span>
                    <button
                      onClick={() => setFormData({...formData, customer_id: ''})}
                      className="ml-auto text-purple-600 hover:text-purple-800 font-bold"
                    >
                      ×
                    </button>
                  </div>
                )}
                <div className="max-h-32 overflow-y-auto border rounded-lg divide-y bg-white">
                  {filteredCustomers.slice(0, 20).map(c => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setFormData({...formData, customer_id: c.id});
                        setCustomerSearch('');
                      }}
                      className={`w-full p-2 text-left hover:bg-gray-50 flex items-center gap-2 ${
                        formData.customer_id === c.id ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs">
                        {c.first_name?.[0]}{c.last_name?.[0]}
                      </div>
                      <span className="text-sm">{c.first_name} {c.last_name}</span>
                      {formData.customer_id === c.id && <span className="ml-auto text-purple-600">✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="h-96 overflow-y-auto border rounded-lg p-4 mb-4 bg-white">
              {chatMessages.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  <Brain className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Starte das Gespräch...</p>
                  <p className="text-sm mt-2">
                    Beispiel: "Erstelle einen Trainingsplan für Muskelaufbau, 3x pro Woche, nur mit Körpergewicht"
                  </p>
                </div>
              )}
              
              {chatMessages.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`mb-4 ${msg.role === 'user' ? 'text-right' : ''}`}
                >
                  <div className={`inline-block max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user' 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                    
                    {msg.plan_data && (
                      <div className="mt-3 p-2 bg-white rounded border">
                        <p className="font-medium text-gray-800 text-xs">
                          ✅ Plan erkannt: {msg.plan_data.plan_name}
                        </p>
                        <Button 
                          size="sm" 
                          className="mt-2 w-full"
                          onClick={() => {
                            setGeneratedPlan(msg.plan_data);
                            setStep(4);
                            setChatMode(false);
                          }}
                        >
                          Plan übernehmen
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="text-left">
                  <div className="inline-block p-3 rounded-lg bg-gray-100">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Beschreibe den gewünschten Trainingsplan..."
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                disabled={loading}
              />
              <Button onClick={sendChatMessage} disabled={loading || !chatInput.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Step 1: Goal Selection */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Schritt 1: Trainingsziel
                </CardTitle>
                <CardDescription>Wähle das Hauptziel für den Trainingsplan</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {GOALS.map(goal => {
                    const Icon = goal.icon;
                    const isSelected = formData.goal === goal.id;
                    return (
                      <button
                        key={goal.id}
                        onClick={() => setFormData({...formData, goal: goal.id})}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          isSelected 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-lg ${goal.bg} flex items-center justify-center mx-auto mb-2`}>
                          <Icon className={`w-6 h-6 ${goal.color}`} />
                        </div>
                        <p className="font-medium">{goal.label}</p>
                      </button>
                    );
                  })}
                </div>
                
                <div className="flex justify-end mt-6">
                  <Button 
                    onClick={() => setStep(2)} 
                    disabled={!formData.goal}
                  >
                    Weiter <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Dumbbell className="w-5 h-5" />
                  Schritt 2: Details
                </CardTitle>
                <CardDescription>Gib weitere Informationen für den Plan an</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Equipment */}
                <div>
                  <Label className="mb-3 block">Ausrüstung</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {EQUIPMENT.map(eq => (
                      <button
                        key={eq.id}
                        onClick={() => setFormData({...formData, equipment: eq.id})}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.equipment === eq.id 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium text-sm">{eq.label}</p>
                        <p className="text-xs text-gray-500">{eq.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Experience */}
                <div>
                  <Label className="mb-3 block">Erfahrungslevel</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {EXPERIENCE.map(exp => (
                      <button
                        key={exp.id}
                        onClick={() => setFormData({...formData, experience_level: exp.id})}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.experience_level === exp.id 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium text-sm">{exp.label}</p>
                        <p className="text-xs text-gray-500">{exp.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Schedule */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Trainingstage pro Woche</Label>
                    <Select 
                      value={String(formData.days_per_week)} 
                      onValueChange={(v) => setFormData({...formData, days_per_week: parseInt(v)})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}x pro Woche</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Trainingsdauer</Label>
                    <Select 
                      value={String(formData.session_duration)} 
                      onValueChange={(v) => setFormData({...formData, session_duration: parseInt(v)})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 Minuten</SelectItem>
                        <SelectItem value="45">45 Minuten</SelectItem>
                        <SelectItem value="60">60 Minuten</SelectItem>
                        <SelectItem value="90">90 Minuten</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Customer Selection with Search */}
                <div>
                  <Label>Kunde (für Zuweisung)</Label>
                  <div className="mt-1 space-y-2">
                    <div className="relative">
                      <Input
                        placeholder="Kunde suchen..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="w-full"
                      />
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                    {formData.customer_id && (
                      <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                        <User className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-900">
                          {customers?.find(c => c.id === formData.customer_id)?.first_name}{' '}
                          {customers?.find(c => c.id === formData.customer_id)?.last_name}
                        </span>
                        <button
                          onClick={() => setFormData({...formData, customer_id: ''})}
                          className="ml-auto text-purple-600 hover:text-purple-800"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                      {filteredCustomers.length === 0 ? (
                        <p className="p-3 text-sm text-gray-500 text-center">
                          {customerSearch ? 'Kein Kunde gefunden' : 'Keine Kunden verfügbar'}
                        </p>
                      ) : (
                        filteredCustomers.slice(0, 50).map(c => (
                          <button
                            key={c.id}
                            onClick={() => {
                              setFormData({...formData, customer_id: c.id});
                              setCustomerSearch('');
                            }}
                            className={`w-full p-2 text-left hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                              formData.customer_id === c.id ? 'bg-purple-50' : ''
                            }`}
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                              {c.first_name?.[0]}{c.last_name?.[0]}
                            </div>
                            <div>
                              <p className="text-sm font-medium">{c.first_name} {c.last_name}</p>
                              <p className="text-xs text-gray-500">{c.email}</p>
                            </div>
                            {formData.customer_id === c.id && (
                              <span className="ml-auto text-purple-600">✓</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Optional fields */}
                <div>
                  <Label>Einschränkungen/Verletzungen (optional)</Label>
                  <Textarea
                    value={formData.restrictions}
                    onChange={(e) => setFormData({...formData, restrictions: e.target.value})}
                    placeholder="z.B. Knieprobleme, Rückenschmerzen..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Besondere Wünsche (optional)</Label>
                  <Textarea
                    value={formData.preferences}
                    onChange={(e) => setFormData({...formData, preferences: e.target.value})}
                    placeholder="z.B. Fokus auf bestimmte Muskelgruppen..."
                    className="mt-1"
                  />
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Zurück
                  </Button>
                  <Button onClick={generatePlan} disabled={!formData.equipment}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Plan generieren
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Loading */}
          {step === 3 && (
            <Card>
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-8 h-8 text-purple-600 animate-pulse" />
                </div>
                <h3 className="text-xl font-semibold mb-2">KI erstellt Trainingsplan...</h3>
                <p className="text-gray-500">Das kann einige Sekunden dauern</p>
                <div className="mt-6">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Review Plan */}
          {step === 4 && generatedPlan && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Generierter Trainingsplan
                </CardTitle>
                <CardDescription>Prüfe den Plan und weise ihn einem Kunden zu</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Plan Overview */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-bold text-lg">{generatedPlan.plan_name}</h3>
                  <p className="text-gray-600">{generatedPlan.description}</p>
                  {generatedPlan.weekly_schedule && (
                    <Badge className="mt-2">{generatedPlan.weekly_schedule}</Badge>
                  )}
                </div>

                {/* Days */}
                {generatedPlan.days?.map((day, dayIdx) => (
                  <Card key={dayIdx}>
                    <CardHeader className="py-3">
                      <CardTitle className="text-base">{day.day_name}</CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <div className="space-y-2">
                        {day.exercises?.map((ex, exIdx) => (
                          <div key={exIdx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div>
                              <p className="font-medium text-sm">{ex.name}</p>
                              <p className="text-xs text-gray-500">{ex.notes}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline">
                                {ex.sets} x {ex.reps}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Tips */}
                {generatedPlan.tips?.length > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-medium mb-2">💡 Tipps</h4>
                    <ul className="space-y-1">
                      {generatedPlan.tips.map((tip, idx) => (
                        <li key={idx} className="text-sm text-gray-700">• {tip}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Customer Selection for Save */}
                {!formData.customer_id && (
                  <div className="p-4 border-2 border-dashed rounded-lg">
                    <Label>Kunde für Zuweisung auswählen</Label>
                    <Select 
                      value={formData.customer_id} 
                      onValueChange={(v) => setFormData({...formData, customer_id: v})}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Kunde wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customers?.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.first_name} {c.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex justify-between pt-4 border-t">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Neu generieren
                  </Button>
                  <Button onClick={savePlan} disabled={loading || !formData.customer_id}>
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Plan speichern & zuweisen
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default TrainingAIAssistant;
